import { TableCell } from '@mui/material';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import logRocketPromise, { LogRocketInstance } from '@/services/logRocket';

interface State {
  readonly error?: Error;
}

interface Props {
  readonly sx?: SystemStyleObject;
  readonly fallbackMessage?: string;
}

export class TableCellErrorBoundary extends React.Component<React.PropsWithChildren<Props>, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error: error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('TableCellErrorBoundary caught an error:', error, errorInfo);
    void logRocketPromise.then((logRocket: LogRocketInstance): void => {
      logRocket.captureException(error, {
        extra: {
          boundary: 'TableCellErrorBoundary',
          componentStack: errorInfo.componentStack ?? '',
          source: 'react_error_boundary',
        },
      });
    });
  }

  render(): React.ReactNode {
    const { children, fallbackMessage, sx } = this.props;
    const { error } = this.state;

    if (error) {
      return (
        <TableCell sx={sx}>
          <Typography variant="body1" color="text.primary" sx={styles.cell}>
            {fallbackMessage || 'Error'}
          </Typography>
        </TableCell>
      );
    }

    return children;
  }
}

const styles: Record<string, SystemStyleObject> = {
  cell: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
};
